/**
 * Metadatos de estado del Aliado (US-2.2) — Admin Web.
 *
 * Mismo contrato que el semáforo del NOC (`station-status.ts`): etiqueta + color +
 * `colorDark` + `iconKey`, para que el consumidor nunca dependa solo del color. Los
 * tipos `AllyStatus` y `AllyTenantSyncStatus` se derivan del contrato OpenAPI en
 * `@goelectric/shared-types`.
 */
import type { AllyStatus, AllyTenantSyncStatus } from '@goelectric/shared-types';
import { darkColors, neutralColors, semanticColors } from '../tokens';
import type { StatusIconKey } from './station-status';

export interface AllyStatusMeta {
  /** Etiqueta visible (obligatoria: no solo color). */
  label: string;
  description: string;
  /** Color para tema claro. */
  color: string;
  /** Color ajustado para tema oscuro. */
  colorDark: string;
  iconKey: StatusIconKey;
}

/** Estado de gestión del aliado (`AllyBase.status`). */
export const ALLY_STATUS_META: Record<AllyStatus, AllyStatusMeta> = {
  ACTIVE: {
    label: 'Activo',
    description: 'Aliado habilitado para operar en la red.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  INACTIVE: {
    label: 'Inactivo',
    description: 'Aliado deshabilitado (no liquida comisión).',
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'offline',
  },
};

/** Estado de sincronización del tenant de CitrineOS del aliado (ADR 0024). */
export const ALLY_TENANT_SYNC_META: Record<AllyTenantSyncStatus, AllyStatusMeta> = {
  PENDIENTE: {
    label: 'Pendiente',
    description: 'Tenant aún no sincronizado con CitrineOS.',
    color: semanticColors.warning,
    colorDark: darkColors.warning,
    iconKey: 'info',
  },
  SYNCED: {
    label: 'Sincronizado',
    description: 'Tenant sincronizado con CitrineOS.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  ERROR: {
    label: 'Error',
    description: 'Falló la sincronización del tenant con CitrineOS.',
    color: semanticColors.danger,
    colorDark: darkColors.danger,
    iconKey: 'warning',
  },
};
